'use client';
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { EnrichedStockData } from '../types/chart';
import { OhlcvIndex } from '../types/chart';
import type { SymbolType, TimeframeType } from '../utils/chartHandlers';
import { calculatedDataBySymbol } from '../utils/chartHandlers';
import { recalculateIndicatorsIncremental } from '../utils/indicatorRecalculator';

/**
 * Candle data Context type
 */
export interface CandleDataContextType {
  /** Full candle data (Symbol → Timeframe → Candles) */
  candleData: Record<SymbolType, Record<TimeframeType, EnrichedStockData[]>>;

  /** Update last candle */
  updateLastCandle: (
    symbol: SymbolType,
    timeframe: TimeframeType,
    priceChange: number,
    volumeIncrement: number,
    options?: { skipIndicatorCalc?: boolean },
  ) => void;

  /** Add new candle */
  addNewCandle: (symbol: SymbolType, timeframe: TimeframeType) => void;

  /** Recalculate indicators in batch (process multiple updates at once) */
  recalculateIndicatorsBatch: (symbol: SymbolType, timeframe: TimeframeType) => void;
}

const CandleDataContext = createContext<CandleDataContextType | undefined>(undefined);

interface CandleDataProviderProps {
  children: ReactNode;
}

/**
 * Dynamic candle data Provider
 * Converts global cache (calculatedDataBySymbol) to React State for dynamic updates
 */
export const CandleDataProvider: React.FC<CandleDataProviderProps> = ({ children }) => {
  // Initialize with deep copy of calculatedDataBySymbol
  const [candleData, setCandleData] = useState<Record<SymbolType, Record<TimeframeType, EnrichedStockData[]>>>(() => {
    return JSON.parse(JSON.stringify(calculatedDataBySymbol));
  });

  /**
   * Update OHLCV values of last candle
   * - Change close value
   * - high = max(current high, new close)
   * - low = min(current low, new close)
   * - volume cumulative increase
   */
  const updateLastCandle = useCallback(
    (
      symbol: SymbolType,
      timeframe: TimeframeType,
      priceChange: number,
      volumeIncrement: number,
      options: { skipIndicatorCalc?: boolean } = {},
    ) => {
      setCandleData(prev => {
        const candles = [...prev[symbol][timeframe]];
        const lastIndex = candles.length - 1;
        const lastCandle = { ...candles[lastIndex] };
        const ohlcv = [...lastCandle.ohlcv] as [number, number, number, number, number, number, number];

        // 가격 업데이트
        const newClose = ohlcv[OhlcvIndex.CLOSE] * (1 + priceChange);
        ohlcv[OhlcvIndex.CLOSE] = newClose;
        ohlcv[OhlcvIndex.HIGH] = Math.max(ohlcv[OhlcvIndex.HIGH], newClose);
        ohlcv[OhlcvIndex.LOW] = Math.min(ohlcv[OhlcvIndex.LOW], newClose);
        ohlcv[OhlcvIndex.VOLUME] += volumeIncrement;

        lastCandle.ohlcv = ohlcv;
        candles[lastIndex] = lastCandle;

        // Recalculate indicators (supports batching option)
        const recalculated = options.skipIndicatorCalc ? candles : recalculateIndicatorsIncremental(candles, lastIndex);

        return {
          ...prev,
          [symbol]: {
            ...prev[symbol],
            [timeframe]: recalculated,
          },
        };
      });
    },
    [],
  );

  /**
   * Create and add new candle
   * - timestamp = previous candle + timeframe (15 min or 1 hour)
   * - open = previous candle's close
   * - high/low/close = open (initial value)
   * - volume = 0
   * - Copy indicator values from previous candle temporarily
   */
  const addNewCandle = useCallback((symbol: SymbolType, timeframe: TimeframeType) => {
    setCandleData(prev => {
      const candles = [...prev[symbol][timeframe]];
      const lastCandle = candles[candles.length - 1];
      const timeframeMs = timeframe === '15m' ? 15 * 60 * 1000 : 60 * 60 * 1000;

      // Copy indicator values from previous candle to maintain chart continuity
      // These will be recalculated accurately in the next batch update
      const newCandle: EnrichedStockData = {
        ohlcv: [
          lastCandle.ohlcv[OhlcvIndex.TIME] + timeframeMs,
          lastCandle.ohlcv[OhlcvIndex.CLOSE], // open
          lastCandle.ohlcv[OhlcvIndex.CLOSE], // high
          lastCandle.ohlcv[OhlcvIndex.CLOSE], // low
          lastCandle.ohlcv[OhlcvIndex.CLOSE], // close
          0, // volume
          0, // quoteVolume
        ],
        ema12: lastCandle.ema12,
        ema26: lastCandle.ema26,
        elder: lastCandle.elder,
        rsi: lastCandle.rsi,
        fullSTO: lastCandle.fullSTO,
      };

      candles.push(newCandle);

      return {
        ...prev,
        [symbol]: {
          ...prev[symbol],
          [timeframe]: candles,
        },
      };
    });
  }, []);

  /**
   * Recalculate indicators in batch (process multiple price updates at once)
   */
  const recalculateIndicatorsBatch = useCallback((symbol: SymbolType, timeframe: TimeframeType) => {
    setCandleData(prev => {
      const candles = prev[symbol][timeframe];
      const lastIndex = candles.length - 1;
      const recalculated = recalculateIndicatorsIncremental(candles, lastIndex);

      return {
        ...prev,
        [symbol]: {
          ...prev[symbol],
          [timeframe]: recalculated,
        },
      };
    });
  }, []);

  const value = useMemo<CandleDataContextType>(
    () => ({
      candleData,
      updateLastCandle,
      addNewCandle,
      recalculateIndicatorsBatch,
    }),
    [candleData, updateLastCandle, addNewCandle, recalculateIndicatorsBatch],
  );

  return <CandleDataContext.Provider value={value}>{children}</CandleDataContext.Provider>;
};

/**
 * Candle data Context Hook
 * Only available within CandleDataProvider
 */
export const useCandleDataStore = () => {
  const context = useContext(CandleDataContext);
  if (!context) {
    throw new Error('useCandleDataStore must be used within CandleDataProvider');
  }
  return context;
};
